import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  TextField,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  FastForward as FastForwardIcon,
  RestartAlt as ResetIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  useGetCurrentTime,
  useSetTime,
  useAdvanceTime,
  useResetTime,
} from '../../../features/development/hooks/useTimeManipulation';
import { formatDistanceToNow } from 'date-fns';

export const TimeDebugSection: React.FC = () => {
  console.log('TimeDebugSection rendering');

  // State
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [customUnits, setCustomUnits] = useState({
    years: 0,
    months: 0,
    weeks: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // Queries and mutations
  const {
    data: timeData,
    isLoading,
    refetch,
  } = useGetCurrentTime({ autoRefresh });
  const setTimeMutation = useSetTime();
  const advanceTimeMutation = useAdvanceTime();
  const resetTimeMutation = useResetTime();

  // Handlers
  const handleManualRefresh = () => {
    refetch();
  };

  const handleToggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const handleSetTime = () => {
    if (!selectedDateTime) return;

    setConfirmDialog({
      open: true,
      title: 'Set Time',
      message: `Set server time to ${selectedDateTime.toLocaleString()}?`,
      onConfirm: () => {
        setTimeMutation.mutate(selectedDateTime);
        setConfirmDialog(null);
      },
    });
  };

  const handleQuickAdvance = (units: {
    hours?: number;
    days?: number;
    weeks?: number;
  }) => {
    const unitLabels: string[] = [];
    if (units.hours) unitLabels.push(`${units.hours} hour(s)`);
    if (units.days) unitLabels.push(`${units.days} day(s)`);
    if (units.weeks) unitLabels.push(`${units.weeks} week(s)`);

    setConfirmDialog({
      open: true,
      title: 'Advance Time',
      message: `Advance time by ${unitLabels.join(', ')}?`,
      onConfirm: () => {
        advanceTimeMutation.mutate(units);
        setConfirmDialog(null);
      },
    });
  };

  const handleCustomAdvance = () => {
    const hasAnyValue = Object.values(customUnits).some(v => v !== 0);
    if (!hasAnyValue) return;

    const unitLabels = Object.entries(customUnits)
      .filter(([, value]) => value !== 0)
      .map(([key, value]) => `${value} ${key}`);

    setConfirmDialog({
      open: true,
      title: 'Advance Time',
      message: `Advance time by ${unitLabels.join(', ')}?`,
      onConfirm: () => {
        advanceTimeMutation.mutate(customUnits);
        setConfirmDialog(null);
        // Reset form
        setCustomUnits({
          years: 0,
          months: 0,
          weeks: 0,
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
      },
    });
  };

  const handleReset = () => {
    setConfirmDialog({
      open: true,
      title: 'Reset Time',
      message: 'Reset to system time? This will clear any time manipulation.',
      onConfirm: () => {
        resetTimeMutation.mutate();
        setConfirmDialog(null);
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(null);
  };

  // Format current time
  const currentTime = timeData?.currentTime
    ? new Date(timeData.currentTime)
    : null;
  const systemTime = timeData?.systemTime
    ? new Date(timeData.systemTime)
    : null;
  const isManipulated = timeData?.isManipulated ?? false;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={3}>
        {/* Warning Banner */}
        {isManipulated && (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            action={
              <Button color="inherit" size="small" onClick={handleReset}>
                Reset
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={600}>
              Time is currently manipulated
            </Typography>
            <Typography variant="caption">
              Server time is not reflecting real time. Click Reset to return to
              system time.
            </Typography>
          </Alert>
        )}

        {/* Current Time Display */}
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon color="primary" />
              <Typography variant="h6">Current Server Time</Typography>
              {isManipulated && (
                <Chip
                  label="MANIPULATED"
                  color="warning"
                  size="small"
                  icon={<WarningIcon />}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip
                title={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              >
                <Chip
                  label={autoRefresh ? 'Auto' : 'Manual'}
                  size="small"
                  color={autoRefresh ? 'success' : 'default'}
                  onClick={handleToggleAutoRefresh}
                />
              </Tooltip>
              <Tooltip title="Refresh now">
                <IconButton size="small" onClick={handleManualRefresh}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          ) : currentTime ? (
            <Stack spacing={1}>
              <Typography variant="h4" fontWeight={700}>
                {currentTime.toLocaleString('en-GB', {
                  dateStyle: 'full',
                  timeStyle: 'long',
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDistanceToNow(currentTime, { addSuffix: true })}
              </Typography>
              {isManipulated && systemTime && (
                <Typography variant="caption" color="warning.main">
                  System time: {systemTime.toLocaleString()} (offset:{' '}
                  {formatDistanceToNow(systemTime, {
                    addSuffix: false,
                    includeSeconds: true,
                  })}
                  )
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="error">
              Failed to load time
            </Typography>
          )}
        </Paper>

        {/* Quick Actions */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Common time adjustments for testing
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FastForwardIcon />}
                onClick={() => handleQuickAdvance({ hours: 1 })}
              >
                +1 Hour
              </Button>
            </Box>
            <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FastForwardIcon />}
                onClick={() => handleQuickAdvance({ days: 1 })}
              >
                +1 Day
              </Button>
            </Box>
            <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FastForwardIcon />}
                onClick={() => handleQuickAdvance({ weeks: 1 })}
              >
                +1 Week
              </Button>
            </Box>
            <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FastForwardIcon />}
                onClick={() => handleQuickAdvance({ days: 30 })}
              >
                +30 Days
              </Button>
            </Box>
            <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FastForwardIcon />}
                onClick={() => handleQuickAdvance({ days: 90 })}
              >
                +90 Days
              </Button>
            </Box>
            <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<ResetIcon />}
                onClick={handleReset}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Set Specific Time */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Set Specific Time</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Set server time to a specific date and time
              </Typography>
              <DateTimePicker
                label="Select Date & Time"
                value={selectedDateTime}
                onChange={setSelectedDateTime}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSetTime}
                disabled={!selectedDateTime || setTimeMutation.isPending}
              >
                Set Time
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Advanced Controls */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Advanced Time Control</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Advance time by multiple units simultaneously
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Years"
                    value={customUnits.years}
                    onChange={e =>
                      setCustomUnits({
                        ...customUnits,
                        years: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Months"
                    value={customUnits.months}
                    onChange={e =>
                      setCustomUnits({
                        ...customUnits,
                        months: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weeks"
                    value={customUnits.weeks}
                    onChange={e =>
                      setCustomUnits({
                        ...customUnits,
                        weeks: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Days"
                    value={customUnits.days}
                    onChange={e =>
                      setCustomUnits({
                        ...customUnits,
                        days: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Hours"
                    value={customUnits.hours}
                    onChange={e =>
                      setCustomUnits({
                        ...customUnits,
                        hours: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Minutes"
                    value={customUnits.minutes}
                    onChange={e =>
                      setCustomUnits({
                        ...customUnits,
                        minutes: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={handleCustomAdvance}
                disabled={advanceTimeMutation.isPending}
              >
                Advance Time
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Confirmation Dialog */}
        {confirmDialog && (
          <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogContent>
              <Typography>{confirmDialog.message}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeConfirmDialog}>Cancel</Button>
              <Button
                onClick={confirmDialog.onConfirm}
                variant="contained"
                color="primary"
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Stack>
    </LocalizationProvider>
  );
};
